import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { copyToClipboard, formatShortId } from '../utils/paymentHelpers';

interface PaymentCopyableIdProps {
  value: string | null | undefined;
  label: string;
  className?: string;
}

export function PaymentCopyableId({ value, label, className }: PaymentCopyableIdProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const full = value?.trim() || null;
  const short = formatShortId(full);

  if (!full) {
    return (
      <div className={cn('flex items-center justify-between gap-2 min-w-0', className)}>
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground italic">
          {t('payments.identity.unavailable')}
        </span>
      </div>
    );
  }

  const handleCopy = async () => {
    const ok = await copyToClipboard(full);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={cn('flex items-center justify-between gap-2 min-w-0', className)}>
      <span className="text-xs font-medium text-muted-foreground shrink-0">{label}</span>
      <div className="flex items-center gap-1.5 min-w-0">
        <code className="truncate font-mono text-xs text-foreground" title={full}>
          {short ?? full}
        </code>
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="shrink-0 rounded-md border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t('payments.detail.copyId', { label })}
        >
          {copied ? t('payments.detail.copied') : t('payments.detail.copy')}
        </button>
      </div>
    </div>
  );
}
